import React from "react";

import { css, cx } from "@emotion/css";
import Slide from "@mui/material/Slide";

import { FontSizes } from "@/assets/fonts.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { ScreenMax } from "@/theme/responsive.ts";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";

// Extra leading on the page-title line that doesn't fit the theme spacing scale.
const TITLE_LINE_HEIGHT_EXTRA = "0.4rem";

export type BasePageDrawerProps = {
  isOpen?: boolean;
  alignWithPageContent?: boolean;
  children: React.ReactElement;
};

export type PageDrawerProps = AtomProps<BasePageDrawerProps, StyleClasses<"root" | "content" | "alignWithPageContent">>;

export const PageDrawer: React.FC<PageDrawerProps> = ({
  id,
  className,
  classes,
  isOpen = false,
  alignWithPageContent = false,
  children,
  ...props
}) => {
  const styles = useStyles(makeStyles);

  // The slide behavior and className are passed to the div element
  return (
    <Slide appear={false} direction="left" in={isOpen} timeout={300} mountOnEnter unmountOnExit {...props}>
      <div
        className={cx(styles.root, className, classes?.content, {
          [styles.alignWithPageContent]: alignWithPageContent,
          [classes?.alignWithPageContent ?? ""]: alignWithPageContent,
        })}
      >
        {children}
      </div>
    </Slide>
  );
};

const makeStyles = (theme: Theme) => {
  const pageTitleHeight = `calc(${FontSizes.h4} + ${TITLE_LINE_HEIGHT_EXTRA} + 2vh + ${theme.spacing(12)})`; // Title + Text line height + margin top + margin bottom
  const sideContentHeight = `calc(100vh - ${pageTitleHeight} - 2vh)`; // Full height - page title height - margin bottom
  const mobileFullHeight = `calc(100vh - ${theme.spacing(30)})`; // Full height minus bottom nav bar
  return {
    root: css({
      display: "flex",
      flex: "0 0 auto",
      margin: `2vh 2vw 2vh 0`,
      maxWidth: "95vw",
      height: "96vh",
      flexDirection: "column",
      overflow: "hidden",
      position: "sticky",
      top: 0,
      zIndex: 10,
      [ScreenMax.SM]: {
        position: "fixed",
        top: 0,
        left: 0,
        margin: 0,
        width: "100%",
        maxWidth: "100%",
        height: mobileFullHeight,
      },
    }),
    alignWithPageContent: css({
      margin: `${pageTitleHeight} 2vw 2vh 0`,
      height: sideContentHeight,
    }),
  };
};
